import { z } from 'zod';
import { CountryCodeSchema, ProvinceCodeSchema } from './aeat-parties';
/**
 * Ficha de tercero (proveedor) — los datos FISCALES de quien nos factura.
 *
 * Hasta ahora un proveedor era dos campos de texto dentro de cada gasto
 * (`supplierName`, `supplierNif`), rellenados por el OCR. Con eso se puede hacer
 * contabilidad, pero no se puede presentar un 347 ni un 190: esos ficheros
 * exigen por tercero el código de provincia de dos dígitos, el país, el NIF-IVA
 * del operador comunitario, la marca de inversión del sujeto pasivo y, cuando
 * hay retención, el nombre partido en apellidos y la subclave de la clave.
 *
 * La ficha es ese sitio. No sustituye a los campos del gasto: el gasto sigue
 * guardando lo que decía EL PAPEL, y la ficha dice lo que sabemos del tercero.
 */
/** Qué le falta a una ficha para poder presentar un modelo con ella. */
export const SupplierGapCodeSchema = z.enum([
    /** Sin NIF no hay registro de declarado posible. */
    'NIF',
    /** Código de provincia de dos dígitos (o 99 si es no residente sin EP). */
    'PROVINCIA',
    /** NIF-IVA del operador comunitario (349 e inversión del sujeto pasivo). */
    'NIF_IVA',
    /** Persona física con retención: el 190 exige apellidos y nombre separados. */
    'NOMBRE_PARTIDO',
    /** Retención sin clave/subclave del 190. */
    'SUBCLAVE',
    /**
     * La provincia contradice al país: un proveedor español con el 99 de «no
     * residente», o uno extranjero con una provincia española. Sin esto la ficha
     * se daba por completa y construía un registro mal en silencio.
     */
    'PROVINCIA_INCOHERENTE',
]);
export const SupplierGapSchema = z.object({
    code: SupplierGapCodeSchema,
    /** Modelos que se quedan sin poder generarse por esta falta. */
    models: z.array(z.string()),
});
const optionalString = (max = 200) => z.string().trim().max(max).nullable().optional();
export const SupplierBaseSchema = z.object({
    /** Razón social o nombre comercial. Es el rótulo de la ficha. */
    name: z.string().trim().min(1).max(300),
    /** false = persona física. Decide cómo se escribe el nombre en el fichero. */
    isCompany: z.boolean().default(true),
    /**
     * Persona física: el 347 y el 190 piden «primer apellido, un espacio, segundo
     * apellido, un espacio y el nombre completo, necesariamente en este orden».
     * Partirlo nosotros a partir de `name` sería adivinar.
     */
    firstName: optionalString(100),
    lastName1: optionalString(100),
    lastName2: optionalString(100),
    nif: optionalString(40),
    country: CountryCodeSchema.default('ES'),
    provinceCode: ProvinceCodeSchema.nullable().optional(),
    /** NIF-IVA intracomunitario con su prefijo de país («IE4276970QH»). */
    vatNumber: optionalString(40),
    address: optionalString(300),
    city: optionalString(120),
    postalCode: optionalString(16),
    /**
     * Establecido en el territorio de aplicación del impuesto. Si es false, el
     * sujeto pasivo de sus servicios somos nosotros (art. 84.Uno.2.º a) LIVA).
     */
    establishedInTai: z.boolean().default(true),
    /**
     * Sus facturas llevan inversión del sujeto pasivo. Se marca a mano para los
     * casos nacionales del art. 84.Uno.2.º (chatarra, ejecuciones de obra); en los
     * no establecidos se deduce y no hace falta tocarlo.
     */
    reverseCharge: z.boolean().default(false),
    /** Clave y subclave del 190 cuando sus facturas llevan retención de IRPF. */
    retentionKey: optionalString(1),
    retentionSubkey: optionalString(2),
    active: z.boolean().default(true),
    notes: optionalString(2000),
});
export const CreateSupplierSchema = SupplierBaseSchema;
export const UpdateSupplierSchema = SupplierBaseSchema.partial();
export const SupplierDtoSchema = SupplierBaseSchema.extend({
    id: z.string(),
    /**
     * Todas las identidades que apuntan a esta ficha: `NIF:<normalizado>` o
     * `NOM:<nombre normalizado>`. Son varias cuando el mismo tercero apareció
     * escrito de formas distintas (con y sin prefijo ES, o antes de que alguien
     * supiera su NIF).
     */
    keys: z.array(z.string()),
    /** Gastos de facturas recibidas atribuidos a esta ficha. */
    expenseCount: z.number().int(),
    /** Base imponible acumulada, en céntimos. */
    baseCents: z.number().int(),
    /** Retención de IRPF acumulada, en céntimos. */
    irpfCents: z.number().int(),
    lastExpenseDate: z.string().nullable(),
    /** Se calcula, no se guarda: depende de para qué modelo haga falta la ficha. */
    gaps: z.array(SupplierGapSchema),
    /**
     * Nombre de otra ficha que PROBABLEMENTE es el mismo tercero: se llaman igual
     * y una tiene NIF y la otra no.
     *
     * Pasa de verdad: el OCR lee el CIF en unas facturas del proveedor y no en
     * otras, y las comisiones de partner nacen siempre sin NIF. El NIF manda sobre
     * el nombre, así que se abren dos fichas y en el 347 serían dos declarados con
     * el total anual partido. No se unen solas —dos proveedores pueden llamarse
     * igual—, pero sí se avisa para que alguien las unifique.
     */
    possibleDuplicateOf: z.string().nullable(),
    /** Quién dio la ficha por revisada, y cuándo. */
    reviewedAt: z.string().nullable(),
    reviewedByName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SupplierListResponseSchema = z.object({
    items: z.array(SupplierDtoSchema),
    total: z.number().int(),
    /** Fichas con alguna falta, de todas las que hay (no solo de esta página). */
    incompleteCount: z.number().int(),
    /**
     * Gastos de factura recibida que todavía no apuntan a ninguna ficha. Debería
     * ser 0; si no lo es, `POST /suppliers/rebuild` lo arregla.
     */
    orphanExpenseCount: z.number().int(),
});
export const MergeSuppliersSchema = z.object({
    /** Ficha que desaparece: sus claves y sus gastos pasan a la que recibe. */
    sourceId: z.string().min(1),
});
// =====================================================================
// Registro de declaraciones presentadas
// =====================================================================
export const TaxDeclarationKindSchema = z.enum(['NORMAL', 'COMPLEMENTARIA', 'SUSTITUTIVA']);
export const TaxDeclarationDtoSchema = z.object({
    id: z.string(),
    /** '303' | '111' | '190' | '347' | '349'. */
    model: z.string(),
    year: z.number().int(),
    /** '1T'…'4T' para los trimestrales, '0A' para los anuales. */
    period: z.string(),
    /**
     * Número identificativo de 13 dígitos. Los tres primeros son el código del
     * modelo; los diez restantes, un secuencial nuestro.
     */
    identifier: z.string(),
    kind: TaxDeclarationKindSchema,
    /** Identificativo de la declaración a la que complementa o sustituye. */
    previousIdentifier: z.string().nullable(),
    recordCount: z.number().int().nullable(),
    totalCents: z.number().int().nullable(),
    generatedAt: z.string(),
    generatedByName: z.string().nullable(),
    presentedAt: z.string().nullable(),
    notes: z.string().nullable(),
});
//# sourceMappingURL=suppliers.js.map